import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type CrossWorkspaceFileDropChoice = "copy" | "reference";

/**
 * The cross-workspace fork (the 2×2's Library-file row): workspaces are
 * separate buildings, so bringing a Library file from another workspace
 * defaults to an independent copy; a live reference is the labeled,
 * deliberate alternative. Same frame and language as LibraryAddModeDialog —
 * only the added from-another-workspace facts and the flipped default
 * distinguish them.
 */
export function CrossWorkspaceFileDropDialog({
  count,
  fileName,
  destination,
  onChoose,
  onClose,
}: {
  count: number;
  /** Shown when count === 1. */
  fileName?: string;
  destination: "canvas" | "chat";
  onChoose: (choice: CrossWorkspaceFileDropChoice) => void;
  onClose: () => void;
}) {
  const what =
    count === 1
      ? `"${fileName ?? "This file"}" is`
      : `These ${count} files are`;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="cross-workspace-drop">
        <DialogHeader>
          {/* A filename is usually one long unbroken token, which has no break
              opportunity and ran straight out of the dialog. `overflow-wrap`
              gives it one; `line-clamp` stops a pathological name from turning
              the header into a paragraph; `leading-snug` because the base
              title is `leading-none`, which collides once it wraps. Done in
              CSS so the title stays a single text node. */}
          <DialogTitle className="line-clamp-3 leading-snug [overflow-wrap:anywhere]">
            {count === 1
              ? `Add "${fileName ?? "this file"}"?`
              : `Add ${count} files?`}
          </DialogTitle>
          <DialogDescription className="[overflow-wrap:anywhere]">
            {what} from a different workspace&apos;s Library. A live reference
            stays linked to that Library — updates show up on this {destination}{" "}
            too, but people here need access to that workspace to open it. A
            copy is independent: changes here never touch the original.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => onChoose("reference")}>
            Add live reference
          </Button>
          <Button onClick={() => onChoose("copy")}>
            {count === 1 ? "Add a copy" : "Add copies"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
